import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { showErrorToast } from '@/components/toast/globalToast';
import { DeveloperAccess } from '@/config/app';
import { listApiKeys } from '@/services/api/apiKeys';
import {
  activeSandboxKeyPrefix,
  SANDBOX_ORDER_STOCK_PER_SKU,
  SandboxExchange,
  sandboxReadiness,
  SandboxReadiness,
} from '@/services/api/sandbox';
import { listWebhookEndpoints } from '@/services/api/webhooks';
import { extractApiErrorMessage } from '@/utils/errorHandler';
import NewSandboxOrderModal from './sandbox/NewSandboxOrderModal';
import NewSandboxShipmentModal from './sandbox/NewSandboxShipmentModal';
import SandboxOrderDetail from './sandbox/SandboxOrderDetail';
import SandboxOrderList from './sandbox/SandboxOrderList';
import SandboxSetupStatus from './sandbox/SandboxSetupStatus';
import SandboxShipmentDetail from './sandbox/SandboxShipmentDetail';
import SandboxShipmentList from './sandbox/SandboxShipmentList';

type DeveloperTabLink = 'keys' | 'webhooks';

/** What the sandbox is about: shipments (the simulator) or warehouse orders. */
type SandboxKind = 'shipments' | 'orders';

const KINDS: {
  key: SandboxKind;
  label: string;
  icon: string;
  title: string;
  desc: string;
}[] = [
  {
    key: 'shipments',
    label: 'Shipments',
    icon: 'ri-truck-line',
    title: 'Sandbox shipments',
    desc: 'Create a shipment and walk it through every status — each step sends a real webhook to your Sandbox endpoint.',
  },
  {
    key: 'orders',
    label: 'Outbound orders',
    icon: 'ri-archive-stack-line',
    title: 'Sandbox outbound orders',
    desc: 'Place a warehouse order with SKUs from your catalog and see the order Dash returns: the hub it picks, out-of-stock answers, cancel.',
  },
];

// ─── Sandbox (real API, sandbox mode) ─────────────────────────────────
// A list of the client's sandbox shipments → a detail page with the
// simulator's action bar (old portal /simulation pattern), the requests this
// page made, and the webhooks each step sent. `?shipment=<id>` selects the
// detail so a refresh or a shared link lands on it.

/** Both sandboxes — the tab before per-client gating (and its tests). */
const ALL_APIS: DeveloperAccess = { shipments: true, orders: true };

const SandboxSimulatorTab: React.FC<{
  /** Which sandboxes this client may use (per-client config). */
  access?: DeveloperAccess;
  /** Jump to the tab that fixes a missing prerequisite. */
  onNavigate?: (tab: DeveloperTabLink) => void;
}> = ({ access = ALL_APIS, onNavigate }) => {
  const [params, setParams] = useSearchParams();
  const shipmentId = params.get('shipment');
  const orderId = params.get('order');
  // An old link (?shipment=…) still lands on the shipments sandbox.
  const requested = params.get('sandbox');
  // Only the sandboxes this client is allowed; with just one there is
  // nothing to choose, and a link to the other one lands on it.
  const kinds = KINDS.filter((k) => access[k.key]);
  const asked: SandboxKind | null =
    requested === 'shipments' || requested === 'orders'
      ? requested
      : shipmentId
        ? 'shipments'
        : orderId
          ? 'orders'
          : null;
  const kind: SandboxKind | null =
    asked && kinds.some((k) => k.key === asked)
      ? asked
      : kinds.length === 1
        ? kinds[0].key
        : null;

  const [readiness, setReadiness] = useState<SandboxReadiness | null>(null);
  const [keyPrefix, setKeyPrefix] = useState<string | null>(null);
  const [endpointUrl, setEndpointUrl] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createOrderOpen, setCreateOrderOpen] = useState(false);
  const [createdExchange, setCreatedExchange] =
    useState<SandboxExchange | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const loadReadiness = useCallback(async () => {
    try {
      const [keys, endpoints] = await Promise.all([
        listApiKeys(),
        listWebhookEndpoints(),
      ]);
      setReadiness(sandboxReadiness(keys, endpoints));
      setKeyPrefix(activeSandboxKeyPrefix(keys));
      setEndpointUrl(
        endpoints.find((e) => e.environment === 'TEST' && !e.disabledAt)?.url ??
          null
      );
    } catch (err) {
      showErrorToast(
        'Failed to check your sandbox setup',
        extractApiErrorMessage(err, 'Failed to check your sandbox setup')
      );
    }
  }, []);

  useEffect(() => {
    loadReadiness();
  }, [loadReadiness]);

  const select = (id: string | null) => {
    const next = new URLSearchParams(params);
    if (id) next.set('shipment', id);
    else next.delete('shipment');
    next.set('tab', 'sandbox');
    next.set('sandbox', 'shipments');
    setParams(next);
  };

  const selectOrder = (id: string | null) => {
    const next = new URLSearchParams(params);
    if (id) next.set('order', id);
    else next.delete('order');
    next.set('tab', 'sandbox');
    next.set('sandbox', 'orders');
    setParams(next);
  };

  const choose = (k: SandboxKind) => {
    const next = new URLSearchParams(params);
    next.delete('shipment');
    next.delete('order');
    next.set('tab', 'sandbox');
    next.set('sandbox', k);
    setCreatedExchange(null);
    setParams(next);
  };

  if (kind === null) {
    return (
      <div className="space-y-4">
        <div>
          <h2 className="text-base font-semibold text-text-strong-950">
            What do you want to try?
          </h2>
          <p className="mt-0.5 text-xs text-text-sub-600">
            Both use the real API in sandbox mode — nothing reaches a hub, a
            warehouse or a rider.
          </p>
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {kinds.map((k) => (
            <button
              key={k.key}
              type="button"
              onClick={() => choose(k.key)}
              className="group flex items-start gap-3 rounded-xl border border-stroke-soft-200 bg-bg-white-0 p-5 text-left shadow-card-xs transition-colors hover:border-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-(--primary-alpha-10) text-primary">
                <i className={`${k.icon} text-lg`} />
              </span>
              <span className="min-w-0">
                <span className="flex items-center gap-1 text-sm font-semibold text-text-strong-950">
                  {k.title}
                  <i className="ri-arrow-right-line text-text-soft-400 transition-transform group-hover:translate-x-0.5" />
                </span>
                <span className="mt-1 block text-xs text-text-sub-600">
                  {k.desc}
                </span>
              </span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  const switcher = !shipmentId && !orderId && kinds.length > 1 && (
    <div
      role="tablist"
      aria-label="Sandbox"
      className="inline-flex rounded-lg border border-stroke-soft-200 bg-bg-white-0 p-0.5"
    >
      {kinds.map((k) => (
        <button
          key={k.key}
          type="button"
          role="tab"
          aria-selected={kind === k.key}
          onClick={() => choose(k.key)}
          className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
            kind === k.key
              ? 'bg-(--primary-alpha-10) text-primary'
              : 'text-text-sub-600 hover:text-text-strong-950'
          }`}
        >
          <i className={k.icon} />
          {k.label}
        </button>
      ))}
    </div>
  );

  if (kind === 'orders') {
    return (
      <div className="space-y-6">
        {orderId ? (
          <SandboxOrderDetail
            key={orderId}
            id={orderId}
            keyPrefix={keyPrefix}
            initialExchange={createdExchange}
            onBack={() => {
              setCreatedExchange(null);
              setReloadKey((k) => k + 1);
              selectOrder(null);
            }}
          />
        ) : (
          <>
            {switcher}
            <div className="flex items-start gap-2.5 rounded-lg bg-warning-lighter px-4 py-3 text-xs text-warning-dark">
              <i className="ri-flask-line mt-px text-sm" />
              <span>
                <b>Real API, sandbox mode.</b> Orders created here are sandbox
                orders: they go to <b>Dash Sandbox Hub</b>, where every SKU of
                your catalog holds <b>{SANDBOX_ORDER_STOCK_PER_SKU}</b> units —
                your real stock is never touched. No warehouse picks them; they
                stay Open until you cancel them. Order status webhooks are not
                available yet.
              </span>
            </div>
            <SandboxOrderList
              onCreate={() => setCreateOrderOpen(true)}
              onOpen={(id) => {
                setCreatedExchange(null);
                selectOrder(id);
              }}
              reloadKey={reloadKey}
            />
          </>
        )}
        <NewSandboxOrderModal
          open={createOrderOpen}
          onClose={() => setCreateOrderOpen(false)}
          onCreated={(id, exchange) => {
            setCreateOrderOpen(false);
            setCreatedExchange(exchange);
            setReloadKey((k) => k + 1);
            selectOrder(id);
          }}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {shipmentId ? (
        <SandboxShipmentDetail
          key={shipmentId}
          id={shipmentId}
          readiness={readiness}
          keyPrefix={keyPrefix}
          endpointUrl={endpointUrl}
          initialExchange={createdExchange}
          onBack={() => {
            setCreatedExchange(null);
            setReloadKey((k) => k + 1);
            select(null);
          }}
          onSetupChanged={loadReadiness}
          onNavigate={onNavigate}
        />
      ) : (
        <>
          {switcher}
          <div className="flex items-start gap-2.5 rounded-lg bg-warning-lighter px-4 py-3 text-xs text-warning-dark">
            <i className="ri-flask-line mt-px text-sm" />
            <span>
              <b>Real API, sandbox mode.</b> Every button sends a real request
              with your login in sandbox mode: shipments created here are
              sandbox shipments, and each <b>Next</b> moves one a step as if the
              hub and a rider had done it — writing its timeline and sending a{' '}
              <b>real webhook to your Sandbox endpoint</b>. Nothing reaches a
              hub, a rider or a delivery.
            </span>
          </div>
          <SandboxSetupStatus readiness={readiness} onNavigate={onNavigate} />
          <SandboxShipmentList
            canCreate={readiness?.ready === true}
            onCreate={() => setCreateOpen(true)}
            onOpen={(id) => {
              setCreatedExchange(null);
              select(id);
            }}
            reloadKey={reloadKey}
          />
        </>
      )}

      <NewSandboxShipmentModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(id, exchange) => {
          setCreateOpen(false);
          setCreatedExchange(exchange);
          setReloadKey((k) => k + 1);
          select(id);
        }}
      />
    </div>
  );
};

export default SandboxSimulatorTab;
